import React from "react";
import { cdnImage, cdnSrcSet } from "@/lib/cdnImage";

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src: string | undefined | null;
  /** Target render width (px); used to ask CDN for a sized variant. */
  w?: number;
  /** Generate a srcset at common breakpoints. Defaults to true. */
  responsive?: boolean;
};

/**
 * `<img>` that routes through the optional CDN image transformer
 * (`cdnImage` — Cloudflare Images / Imgix / Cloudinary).
 *
 * Adds sensible defaults for performance: `loading="lazy"`, `decoding="async"`,
 * and a responsive `srcset` when a URL is provided. When no CDN base is
 * configured the URL is returned untouched, so dev / preview keeps working.
 */
export const ProductImg = React.forwardRef<HTMLImageElement, Props>(function ProductImg(
  { src, w, responsive = true, loading, decoding, ...rest },
  ref,
) {
  const url = cdnImage(src, w ? { width: w } : {});
  const srcSet = responsive && src ? cdnSrcSet(src) : undefined;
  return (
    <img
      ref={ref}
      src={url}
      srcSet={srcSet || undefined}
      loading={loading ?? "lazy"}
      decoding={decoding ?? "async"}
      {...rest}
    />
  );
});